import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';

// Pages
import LandingPage from './pages/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import AuthCallback from './pages/AuthCallback';
import BrowsePage from './pages/BrowsePage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import DashboardPage from './pages/DashboardPage';
import MessagesPage from './pages/MessagesPage';
import CreateListingPage from './pages/CreateListingPage';
import { PaymentPage, PaymentSuccessPage } from './pages/PaymentPage';
import PaymentSettingsPage from './pages/PaymentSettingsPage';
import RentalCheckoutPage from './pages/RentalCheckoutPage';
import SavedPage from './pages/SavedPage';
import ProfilePage from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';

// New Pages (Footer Links)
import HowItWorksPage from './pages/HowItWorksPage';
import FAQPage from './pages/FAQPage';
import OwnerGuidePage from './pages/OwnerGuidePage';
import HelpCenterPage from './pages/HelpCenterPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminForgotPasswordPage from './pages/admin/AdminForgotPasswordPage';
import AdminResetPasswordPage from './pages/admin/AdminResetPasswordPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminContentPage from './pages/admin/AdminContentPage';
import AdminIntegrationsPage from './pages/admin/AdminIntegrationsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminWebhooksPage from './pages/admin/AdminWebhooksPage';
import AdminMetadataPage from './pages/admin/AdminMetadataPage';
import AdminAgencyReviewPage from './pages/admin/AdminAgencyReviewPage';

// Handle auth callback with session_id in URL fragment
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id - detect this BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/property/:id" element={<PropertyDetailPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/messages/:conversationId" element={<MessagesPage />} />
      <Route path="/create-listing" element={<CreateListingPage />} />
      <Route path="/edit-listing/:id" element={<CreateListingPage />} />
      <Route path="/payment/:bookingId" element={<PaymentPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/settings" element={<PaymentSettingsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/saved" element={<SavedPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/profile/:userId" element={<ProfilePage />} />
      
      {/* Footer-Linked Pages */}
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/owner-guide" element={<OwnerGuidePage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="content" element={<AdminContentPage />} />
        <Route path="agency-review" element={<AdminAgencyReviewPage />} />
        <Route path="integrations" element={<AdminIntegrationsPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="webhooks" element={<AdminWebhooksPage />} />
        <Route path="metadata" element={<AdminMetadataPage />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              className: 'font-body'
            }}
          />
        </AdminProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
